<template>
  <div class="container-page">
    <div>
      <p class="title-inner">Организации и ФОИВ</p>
      <template v-if="isAccessAllowed">
        <div class="mb-16">
          <ButtonComponent
            v-permission="permissions.refresh"
            mod="gradient-bg"
            class="ml-2"
            :disabled="importInProgress"
            @click="refresh"
          >
            {{ !importInProgress ? 'Обновить' : 'Загрузка...' }}
          </ButtonComponent>
        </div>
        <div class="mb-4">
          <DefaultFilter :filterValue="filter" @loadFilter="onLoadFilter" />
        </div>
        <div class="table-container mb-16 js-table-arrow">
          <table class="stockTable">
            <thead>
              <tr>
                <th></th>
                <th>
                  <SortControl :order="getSortingState(SortColumnMap.Sub)" @click="changeSorting(SortColumnMap.Sub)">
                    ID
                  </SortControl>
                </th>
                <th>
                  <SortControl :order="getSortingState(SortColumnMap.Name)" @click="changeSorting(SortColumnMap.Name)">
                    Название
                  </SortControl>
                </th>
                <th>
                  <SortControl
                    :order="getSortingState(SortColumnMap.FullName)"
                    @click="changeSorting(SortColumnMap.FullName)"
                  >
                    Полное название
                  </SortControl>
                </th>
                <th>
                  <SortControl :order="getSortingState(SortColumnMap.Type)" @click="changeSorting(SortColumnMap.Type)">
                    Тип
                  </SortControl>
                </th>
                <th>
                  <SortControl
                    :order="getSortingState(SortColumnMap.Status)"
                    @click="changeSorting(SortColumnMap.Status)"
                  >
                    Статус
                  </SortControl>
                </th>
                <th>Дата создания</th>
                <th>Дата изменения</th>
              </tr>
              <tr>
                <th></th>
                <th></th>
                <th class="filter" colspan="2">
                  <FilterInput v-model="filter.name" placeholder="Найти..." @valueChangedDebounced="changeFilter" />
                </th>
                <th class="filter">
                  <FilterInput
                    type="select2"
                    :select2Options="orgTypes"
                    :select2Settings="select2SettingsMulti"
                    v-model="filter.typeIds"
                    multiple
                    placeholder="(Все)"
                    @valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterActiveInput
                    v-model="filter.isActive"
                    placeholder="(Все)"
                    @valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter date-w-135">
                  <FilterInput
                    type="date"
                    modClass="filter-small"
                    v-model="filter.createdStart"
                    @valueChangedDebounced="changeFilter"
                    :upperLimit="upperLimitCreationStart"
                    dateIcon
                  />
                  <FilterInput
                    type="date"
                    modClass="filter-small"
                    v-model="filter.createdEnd"
                    @valueChangedDebounced="changeFilter"
                    :upperLimit="upperLimitEnd"
                    :lowerLimit="lowerLimitCreationEnd"
                    dateIcon
                  />
                </th>
                <th class="filter date-w-135">
                  <FilterInput
                    type="date"
                    modClass="filter-small"
                    v-model="filter.modifiedStart"
                    @valueChangedDebounced="changeFilter"
                    :upperLimit="upperLimitModifiedStart"
                    dateIcon
                  />
                  <FilterInput
                    type="date"
                    modClass="filter-small"
                    v-model="filter.modifiedEnd"
                    @valueChangedDebounced="changeFilter"
                    :upperLimit="upperLimitEnd"
                    :lowerLimit="lowerLimitModifiedEnd"
                    dateIcon
                  />
                </th>
              </tr>
            </thead>
            <tbody v-if="page.items?.length && !isLoading">
              <tr v-for="item in page.items" :key="item.id" @dblclick="editCard(item.id)">
                <td>
                  <IconComponent v-if="canEdit" @click="editCard(item.id)" name="mode" />
                </td>
                <td>{{ item.sub }}</td>
                <td>{{ item.name }}</td>
                <td>{{ item.nameFull }}</td>
                <td>{{ item.organizationType.name }}</td>
                <td>{{ item.isActive ? 'действ.' : 'не действ.' }}</td>
                <td class="table-sort__td column-date-creation">
                  <p>
                    {{ $momentFormat(item?.created, DATE_FORMATS['DD.MM.YYYY']) }}
                    <br />
                    {{ $momentFormat(item?.created, DATE_FORMATS['HH:mm']) }}
                  </p>
                </td>
                <td class="table-sort__td column-date-change">
                  <p>
                    {{ $momentFormat(item?.modified, DATE_FORMATS['DD.MM.YYYY']) }}
                    <br />
                    {{ $momentFormat(item?.modified, DATE_FORMATS['HH:mm']) }}
                  </p>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="isLoading">
              <tr>
                <td class="text-center" colspan="8">Загрузка...</td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td class="text-center" colspan="8">Нет данных.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="page.pagesTotal > 1">
          <Pagination
            :pageCount="page.pagesTotal"
            :currentPage="page.pageNumber"
            @change="onPageChange($event, false)"
            @more="onPageChange($event, true)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import DefaultFilter from '@/common/components/DefaultFilter.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import SortControl from '@/common/components/SortControl.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import IconComponent from '@/common/ui/IconComponent.vue';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';

  import dateFormat from '@/modules/navigator/mixin/dateFormat.js';

  import FilterActiveInput from '../../../common/components/FilterActiveInput.vue';
  import FilterInput from '../../../common/components/FilterInput.vue';
  import API from '../api/organization';
  import { SORT_COLUMN_ORGANIZATIONS } from '../constants/organizations';

  const PERMISSIONS = {
    view: Constants.Permissions.OrgView,
    edit: Constants.Permissions.OrgEdit,
    refresh: Constants.Permissions.OrganizationsRefresh,
  };

  export default {
    name: 'OrganizationsPage',
    components: {
      SortControl,
      FilterActiveInput,
      FilterInput,
      ButtonComponent,
      IconComponent,
      DefaultFilter,
    },
    mixins: [security, dateFormat],
    data() {
      return {
        SortColumnMap: SORT_COLUMN_ORGANIZATIONS,
        defaultTypeIds: this.isAdminSection()
          ? [
              Constants.organizationType.foiv,
              Constants.organizationType.external,
              Constants.organizationType.regionalOrInternational,
              Constants.organizationType.subjectRF,
            ]
          : [Constants.organizationType.navigatorParticipant],
        request: {
          pageNumber: 1,
          pageSize: Constants.pageSize,
          typeIds: [],
        },
        filter: {
          name: null,
          typeIds: null,
          isActive: null,
          createdStart: '',
          createdEnd: '',
          modifiedStart: '',
          modifiedEnd: '',
          sortColumn: null,
          isDescending: null,
        },
        page: {},
        isLoading: false,
        canEdit: false,
        orgTypes: [],
        select2SettingsMulti: Constants.select2SettingsMultiple,
        importInProgress: false,
        DATE_FORMATS: Constants.DATE_FORMATS,
      };
    },
    async created() {
      const isAllowed = await this.checkPermissionAndRedirect(PERMISSIONS.view);
      if (!isAllowed) return;

      this.checkPermission(PERMISSIONS.edit).then((response) => {
        this.canEdit = response.data;
      });
      loadFilter(this.$options.name).then((response) => {
        let filter = response;
        if (filter) {
          this.filter = filter;
        } else {
          this.filter.sortColumn = SORT_COLUMN_ORGANIZATIONS.Name;
          this.filter.isDescending = false;
        }
        this.loadDictionaries();
        this.loadPage();
      });
    },
    computed: {
      permissions() {
        return PERMISSIONS;
      },
    },
    methods: {
      onPageChange(pageNumber, addToExist) {
        this.request.pageNumber = pageNumber;
        this.loadPage(addToExist);
      },
      loadPage(append) {
        this.isLoading = !append;
        let request = Object.assign({}, Object.assign(this.request, this.filter));
        if (!request.typeIds?.length) {
          request.typeIds = this.defaultTypeIds;
        }
        API.search(request)
          .then((response) => {
            if (!append) {
              this.page = {};
              this.$nextTick(() => {
                this.page = response.data;
              });
            } else if (response.data) {
              //добавляем к существующему
              this.page.pageNumber = response.data.pageNumber;
              this.page.items = this.page.items.concat(response.data.items);
            }
            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
      },
      loadDictionaries() {
        API.getOrgTypes()
          .then((response) => {
            this.orgTypes = response.data
              .filter((x) => this.defaultTypeIds.includes(x.id))
              .map((x) => ({ text: x.name, id: x.id }));
          })
          .catch((error) => {
            console.error(error?.response?.data);
          });
      },
      editCard(id) {
        const action = this.canEdit ? 'edit' : 'view';
        const routeName = this.isAdminSection() ? 'OrganizationCard' : 'NavOrgCard';
        this.$router.push({ name: routeName, params: { id: id, action: action } });
      },
      refresh() {
        const options = {
          title: 'Обновление',
          text: 'Вы действительно хотите обновить организации данными от внешнего сервиса?',
          confirmButtonText: 'Обновить',
          cancelButtonText: 'Отмена',
          showCancelButton: true,
          icon: 'warning',
        };
        Constants.alert.fire(options).then((res) => {
          if (res.isConfirmed) {
            this.importInProgress = true;
            API.refresh()
              .then((response) => {
                this.importInProgress = false;
                this.loadPage();
                let message = `Обновление завершено.<br/>Обновлено записей: ${response.data.updatedCount}`;
                if (response.data.updatedCount === 0 && response.data.isNetworkError) {
                  message +=
                    '<br/><br/>Ни одной записи не было обновлено. Возможно, сервис ЕГРЮЛ недоступен.<br/>Подробности ошибок см. в логе сервиса.';
                }
                Constants.alert.fire('Обновление', message, response.data.updatedCount > 0 ? 'success' : 'warning');
              })
              .catch(() => {
                this.importInProgress = false;
                Constants.alert.fire('Обновление', 'Ошибка при обновлении организаций.', 'error');
              });
          }
        });
      },
      isAdminSection() {
        return this.$route.path.indexOf('admin') > -1;
      },
      changeFilter() {
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filter);
        this.loadPage(false);
      },
      onLoadFilter(item) {
        this.filter = item;
        this.changeFilter();
      },
      changeSorting(columnId) {
        if (this.filter.sortColumn === columnId) {
          this.filter.isDescending = !this.filter.isDescending;
        } else {
          this.filter.sortColumn = columnId;
          this.filter.isDescending = false;
        }
        this.changeFilter();
      },
      getSortingState(columnId) {
        if (this.filter.sortColumn !== columnId) {
          return null;
        }
        return this.filter.isDescending ? 'descending' : 'ascending';
      },
    },
  };
</script>

<style scoped>
  .stockTable {
    min-height: 65vh;
  }
</style>
